import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import type { User } from 'firebase/auth';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithPopup,
  sendPasswordResetEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
} from 'firebase/auth';
import {
  doc,
  getDoc,
  setDoc,
  collection,
  query,
  where,
  getDocs,
  updateDoc,
  deleteDoc,
  Timestamp,
  serverTimestamp,
  addDoc,
  writeBatch,
} from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import { detectBrowserTimeZone } from '../lib/utils';
import { mfaSession as mfaSessionApi } from '../services/mfaService';
import { mfaTokenStore } from '../services/mfaTokenStore';
import type {
  UserProfile,
  Organization,
  OrganizationMembership,
  UserRole,
  Permission,
  Invitation,
} from '../types/auth';
import { hasPermission as checkPermission } from '../types/auth';
import {
  CURRENT_LEGAL_VERSION,
  ORG_LEGAL_VERSION,
  acceptedDocsSnapshot,
  legalIsCurrent,
} from '../legal/legalVersions';
import { computeLegalContentHashes } from '../legal/legalContentHashes';
import { API_URL } from '@/config/env';

/**
 * True once the user has accepted the legal documents to the version currently
 * required by the gate.
 *
 * By default (REQUIRE_REACCEPT_VERSION === null) ANY prior acceptance counts —
 * we do NOT re-prompt on a version bump; updated documents are communicated by
 * notice/email and continued use constitutes acceptance (see each document's
 * "Changes" section). When a material change is signed off and
 * REQUIRE_REACCEPT_VERSION is set, an older acceptance no longer counts and the
 * gate re-fires. See legalVersions.ts `legalIsCurrent`.
 */
function hasAcceptedLegal(profile: UserProfile | null): boolean {
  // Legacy records may carry acceptedAt without eulaVersion. They count as an
  // UNVERSIONED acceptance: valid under the default "accepted at least once"
  // gate, but re-gated when REQUIRE_REACCEPT_VERSION is set — we can't prove
  // which version of the text they accepted (see legalIsCurrent).
  return legalIsCurrent(profile?.legal?.eulaVersion, !!profile?.legal?.acceptedAt);
}

// Email a magic link was requested for, kept so the return visit on the same
// device completes sign-in without re-typing it (Firebase requires the email
// again when redeeming the link).
const MAGIC_LINK_EMAIL_KEY = 'simcheck.auth.magicLinkEmail';

// ============================================================================
// CONTEXT TYPE
// ============================================================================

interface AuthContextType {
  // User state
  currentUser: User | null;
  user: User | null; // Alias for compatibility
  userProfile: UserProfile | null;
  loading: boolean;

  // Organization state
  organizations: OrganizationMembership[];
  activeOrganization: Organization | null;
  activeOrganizationId: string | null;
  userRole: UserRole | null;
  organizationsLoading: boolean;

  // RBAC: System role state
  systemRole: 'super_admin' | 'support' | null;
  isSystemAdmin: boolean;
  isSuperAdmin: boolean;
  isSupport: boolean;
  hasSystemRole: (role: 'super_admin' | 'support') => boolean;

  // Org selection state
  needsOrgSelection: boolean;

  // Legal acceptance gate (first login + on version bump)
  needsLegalAcceptance: boolean;
  acceptLegal: () => Promise<void>;

  // Two-factor auth gate. `mfaBlocking` = full-screen gate (required, past
  // grace, or user-enrolled-but-untrusted-device); `mfaNag` = soft banner
  // during the grace window.
  mfaBlocking: boolean;
  mfaNag: boolean;
  mfaEnrollmentRequired: boolean;
  mfaMethods: string[];
  /** Recovery codes exist, so the gate can offer them as a way through. */
  mfaRecoveryAvailable: boolean;
  mfaGraceEndsAt: number | null;
  onMfaSatisfied: (token: string, expiresAtMs: number) => void;
  refreshMfaSession: () => Promise<void>;

  // View as Organization state
  isViewingAsOrg: boolean;
  viewedAsOrg: Organization | null;

  // Auth methods
  login: (email: string, password: string) => Promise<void>;
  signup: (email: string, password: string, displayName?: string) => Promise<void>;
  loginWithGoogle: (acceptedLegalVersion?: string) => Promise<void>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  sendMagicLink: (email: string) => Promise<void>;
  completeMagicLink: (emailOverride?: string) => Promise<void>;
  isMagicLinkUrl: () => boolean;

  // Organization methods
  switchOrganization: (organizationId: string) => Promise<void>;
  createOrganization: (name: string) => Promise<string>;
  updateOrganization: (updates: Partial<Organization>) => Promise<void>;
  refreshOrganizations: () => Promise<void>;

  // View as Organization methods
  viewAsOrganization: (organizationId: string, reason: string) => Promise<void>;
  stopViewingAsOrg: () => Promise<void>;

  // User management methods
  inviteUser: (email: string, role: Exclude<UserRole, 'owner'>) => Promise<void>;
  removeUser: (userId: string) => Promise<void>;
  updateUserRole: (userId: string, role: UserRole) => Promise<void>;
  acceptInvitation: (token: string) => Promise<void>;
  getPendingInvitations: () => Promise<Invitation[]>;
  getPendingInvitationsForOrg: (organizationId: string) => Promise<Invitation[]>;
  revokeInvitation: (invitationId: string) => Promise<void>;

  // Permission helpers
  hasPermission: (permission: Permission) => boolean;
  canManageUsers: () => boolean;
  canManageResources: () => boolean;
  canEditResource: (createdBy: string) => boolean;
  canDeleteResource: (createdBy: string) => boolean;

  // Profile helpers
  refreshUserProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

// ============================================================================
// CUSTOM HOOK
// ============================================================================

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// ============================================================================
// AUTH PROVIDER COMPONENT
// ============================================================================

export function AuthProvider({ children }: { children: React.ReactNode }) {
  // Firebase auth user
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  // Organization state
  const [organizations, setOrganizations] = useState<OrganizationMembership[]>([]);
  const [activeOrganization, setActiveOrganization] = useState<Organization | null>(null);
  const [activeOrganizationId, setActiveOrganizationId] = useState<string | null>(null);
  const [userRole, setUserRole] = useState<UserRole | null>(null);
  const [organizationsLoading, setOrganizationsLoading] = useState(false);

  // RBAC: System role state (computed from userProfile)
  const systemRole = (userProfile as any)?.systemRole || null;
  const isSystemAdmin = systemRole !== null;
  const isSuperAdmin = systemRole === 'super_admin';
  const isSupport = systemRole === 'support';

  // Org selection state
  const [needsOrgSelection, setNeedsOrgSelection] = useState(false);

  // Legal acceptance gate state — set from the loaded profile in
  // onAuthStateChanged; cleared by acceptLegal() and on logout.
  const [needsLegalAcceptance, setNeedsLegalAcceptance] = useState(false);

  // Two-factor auth gate state — driven by the /auth/mfa/session bootstrap
  // (mfaSessionEffect below) and the api.ts 403 mfa_required fallback.
  const [mfaBlocking, setMfaBlocking] = useState(false);
  const [mfaNag, setMfaNag] = useState(false);
  const [mfaEnrollmentRequired, setMfaEnrollmentRequired] = useState(false);
  const [mfaMethods, setMfaMethods] = useState<string[]>([]);
  const [mfaRecoveryAvailable, setMfaRecoveryAvailable] = useState(false);
  const [mfaGraceEndsAt, setMfaGraceEndsAt] = useState<number | null>(null);

  // View as Organization state
  const [isViewingAsOrg, setIsViewingAsOrg] = useState(false);
  const [viewedAsOrg, setViewedAsOrg] = useState<Organization | null>(null);

  // CRITICAL: Flag to track when loginWithGoogle is processing
  // This prevents onAuthStateChanged from processing new users until loginWithGoogle completes
  const loginProcessingRef = React.useRef<boolean>(false);

  // UID whose session is fully established (profile + org resolved successfully).
  // onAuthStateChanged re-fires on every silent token refresh (~hourly); if a
  // Firestore read transiently fails during one of those re-runs, the catch must
  // NOT sign out an already-working session (that's the "randomly kicked back to
  // login mid-session" bug). Set on successful establishment; checked in catch.
  const establishedUidRef = React.useRef<string | null>(null);

  // Legal consent captured on the registration form (Signup page checkbox),
  // carried across to the server-side invite-accept so it can stamp the user's
  // (and, for owner/admin, the org's) legal acceptance from the SAME consent the
  // user gave on the form — no second "accept terms" gate for fresh registrants.
  // Set by signup() / loginWithGoogle(consent); consumed + cleared by the
  // onAuthStateChanged invite-accept branch.
  const pendingConsentRef = React.useRef<{ version: string; docs: Record<string, string> } | null>(null);

  // ============================================================================
  // HELPER FUNCTIONS
  // ============================================================================

  const generateSlug = (name: string): string => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  };

  const generateInvitationToken = (): string => {
    return Array.from(crypto.getRandomValues(new Uint8Array(32)))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  };

  // ============================================================================
  // USER PROFILE MANAGEMENT
  // ============================================================================

  const createUserProfile = async (user: User, displayName?: string, legalVersion?: string) => {
    const userRef = doc(db, 'users', user.uid);
    const userDoc = await getDoc(userRef);

    // When the caller passes consent captured on the signup form, stamp the legal
    // record HERE — synchronously in the signup flow, at the moment the profile is
    // created — rather than relying on a ref surviving the race to the async
    // server-side accept. This guarantees the profile is never persisted without a
    // legal record, so the post-login gate cannot appear for someone who already
    // ticked the terms box. (The server-side accept still stamps it as a backstop
    // and binds the org MSA.)
    const legalStamp = legalVersion
      ? {
          eulaVersion: legalVersion,
          acceptedDocs: acceptedDocsSnapshot(),
          acceptedAt: serverTimestamp() as Timestamp,
          ip: null as string | null,
        }
      : null;

    if (!userDoc.exists()) {
      const newProfile: Omit<UserProfile, 'id'> = {
        email: user.email!,
        displayName: displayName || user.displayName || null,
        photoURL: user.photoURL || null,
        phoneNumber: user.phoneNumber || null,
        createdAt: serverTimestamp() as Timestamp,
        lastLoginAt: serverTimestamp() as Timestamp,
        preferences: {
          theme: 'dark',
          language: 'en',
          // Stamp the browser-detected zone so a user who never opens Settings
          // still sees local time, not UTC. They can override it in Settings.
          timezone: detectBrowserTimeZone(),
          notifications: true,
        },
        metadata: {
          emailVerified: user.emailVerified,
          provider: user.providerData[0]?.providerId || 'email',
          lastOrganizationId: null,
        },
        ...(legalStamp ? { legal: legalStamp } : {}),
      } as Omit<UserProfile, 'id'>;
      await setDoc(userRef, newProfile);
      return { id: user.uid, ...newProfile } as UserProfile;
    } else {
      // Existing profile: refresh last login, and backfill the legal stamp if
      // consent was given and none is recorded yet.
      const data = userDoc.data();
      const updates: any = { lastLoginAt: serverTimestamp() };
      if (legalStamp && !data.legal) {
        updates.legal = legalStamp;
      }
      await updateDoc(userRef, updates);
      return { id: user.uid, ...data, ...(updates.legal ? { legal: updates.legal } : {}) } as UserProfile;
    }
  };

  const loadUserProfile = async (user: User) => {
    const userRef = doc(db, 'users', user.uid);
    const userDoc = await getDoc(userRef);

    if (userDoc.exists()) {
      return { id: user.uid, ...userDoc.data() } as UserProfile;
    }
    return null;
  };

  // ============================================================================
  // ORGANIZATION MANAGEMENT
  // ============================================================================

  const loadUserOrganizations = async (userId: string): Promise<OrganizationMembership[]> => {
    const orgsRef = collection(db, 'users', userId, 'organizations');
    const orgsSnapshot = await getDocs(orgsRef);

    // Get basic membership data
    const memberships = orgsSnapshot.docs.map((doc) => ({
      organizationId: doc.id,
      ...(doc.data() as Omit<OrganizationMembership, 'organizationId'>),
    }));

    // Fetch actual organization names from the organizations collection
    // to ensure we have the correct names (not stale/placeholder data).
    //
    // Prune DANGLING memberships: if the org doc no longer exists (org was
    // deleted but this mirror wasn't cleaned up), drop the membership entirely.
    // Otherwise switchOrganization() throws 'Organization not found' on it and
    // the whole login is signed out — one deleted org strands the user. A
    // read ERROR (permission hiccup / transient) is NOT a definitive "missing",
    // so we keep the membership in that case rather than dropping on a blip.
    const enrichedMemberships = await Promise.all(
      memberships.map(async (membership) => {
        try {
          const orgRef = doc(db, 'organizations', membership.organizationId);
          const orgDoc = await getDoc(orgRef);
          if (orgDoc.exists()) {
            const orgData = orgDoc.data();
            return {
              ...membership,
              organizationName: orgData.name || membership.organizationName,
            };
          }
          // Definitively gone → dangling membership; drop it.
          console.warn(`[loadUserOrganizations] dropping dangling membership for missing org ${membership.organizationId}`);
          return null;
        } catch (error) {
          console.error(`Error fetching org ${membership.organizationId}:`, error);
          return membership; // uncertain (read error) → keep, don't drop on a blip
        }
      })
    );

    return enrichedMemberships.filter((m): m is OrganizationMembership => m !== null);
  };

  const loadOrganizationDetails = async (organizationId: string): Promise<Organization | null> => {
    const orgRef = doc(db, 'organizations', organizationId);
    const orgDoc = await getDoc(orgRef);

    if (orgDoc.exists()) {
      return { id: organizationId, ...orgDoc.data() } as Organization;
    }
    return null;
  };

  const loadUserRoleInOrganization = async (
    organizationId: string,
    userId: string
  ): Promise<UserRole | null> => {
    const memberRef = doc(db, 'organizations', organizationId, 'members', userId);
    const memberDoc = await getDoc(memberRef);

    if (memberDoc.exists()) {
      return memberDoc.data().role as UserRole;
    }
    return null;
  };

  const repairMembershipsIfNeeded = async () => {
    try {
      const idToken = await auth.currentUser?.getIdToken();
      if (!idToken) return;

      const API_BASE = API_URL;
      const resp = await fetch(`${API_BASE}/organizations/repair-memberships`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' },
      });
      if (resp.ok) {
        const data = await resp.json();
        if (data.repaired > 0) {
          console.info(`[Auth] Membership repair: ${data.message}`);
        }
      }
    } catch (err) {
      // Non-fatal — don't block login
      console.error('[Auth] Membership repair failed (non-fatal):', err);
    }
  };

  const refreshOrganizations = useCallback(async () => {
    if (!currentUser) return;

    setOrganizationsLoading(true);
    try {
      const userOrgs = await loadUserOrganizations(currentUser.uid);
      setOrganizations(userOrgs);

      // Repair any missing organizations/{orgId}/members/{uid} docs before switching
      await repairMembershipsIfNeeded();

      // If no active organization, auto-select or ask user to choose
      if (!activeOrganizationId && userOrgs.length > 0) {
        const lastOrgId = userProfile?.metadata.lastOrganizationId;
        const hasPreference = lastOrgId && userOrgs.find((o) => o.organizationId === lastOrgId);

        if (userOrgs.length === 1 || hasPreference) {
          // Single org, or user has a previously selected org — auto-select
          const orgToActivate = hasPreference ? lastOrgId! : userOrgs[0].organizationId;
          await switchOrganization(orgToActivate);
        } else {
          // Multiple orgs, no prior preference — let the user choose
          setNeedsOrgSelection(true);
        }
      }
    } catch (error) {
      console.error('Error loading organizations:', error);
    } finally {
      setOrganizationsLoading(false);
    }
  }, [currentUser, activeOrganizationId, userProfile]);

  // ============================================================================
  // AUTH METHODS
  // ============================================================================

  const login = async (email: string, password: string) => {
    await signInWithEmailAndPassword(auth, email, password);
  };

  // Passwordless (magic link) sign-in — send half. The server generates the
  // one-time link (Admin SDK) and emails it via Brevo; Firebase's default
  // mailer (sendSignInLinkToEmail) is NOT used because its unbranded
  // firebaseapp.com sender lands in Gmail spam. The link lands back on /login,
  // where completeMagicLink() redeems it.
  const sendMagicLink = async (email: string) => {
    const API_BASE = API_URL;
    const resp = await fetch(`${API_BASE}/auth/magic-link`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, redirectUrl: `${window.location.origin}/login` }),
    });
    if (!resp.ok) {
      const data = await resp.json().catch(() => ({}));
      throw new Error(data.message || `Could not send the sign-in link (${resp.status})`);
    }
    window.localStorage.setItem(MAGIC_LINK_EMAIL_KEY, email.toLowerCase());
  };

  const isMagicLinkUrl = () => isSignInWithEmailLink(auth, window.location.href);

  // Passwordless sign-in — redeem half. Mirrors loginWithGoogle's invite-only
  // gating: existing users (profile doc or org membership) pass straight
  // through; new emails need a pending invitation or they're signed back out.
  const completeMagicLink = async (emailOverride?: string) => {
    const email = emailOverride || window.localStorage.getItem(MAGIC_LINK_EMAIL_KEY);
    if (!email) {
      // Link opened on a different device/browser than the one that requested
      // it — the caller must collect the email and retry with emailOverride.
      const err: any = new Error('Confirm your email address to finish signing in.');
      err.code = 'auth/missing-email';
      throw err;
    }

    // Hold off onAuthStateChanged's bootstrap until the invite check decides
    // whether this user is allowed in (same pattern as loginWithGoogle).
    loginProcessingRef.current = true;
    try {
      const userCredential = await signInWithEmailLink(auth, email, window.location.href);
      window.localStorage.removeItem(MAGIC_LINK_EMAIL_KEY);

      const userRef = doc(db, 'users', userCredential.user.uid);
      const userDoc = await getDoc(userRef);
      if (userDoc.exists()) {
        // Existing user — let onAuthStateChanged handle the rest
        loginProcessingRef.current = false;
        return;
      }

      const existingOrgs = await loadUserOrganizations(userCredential.user.uid);
      if (existingOrgs.length > 0) {
        loginProcessingRef.current = false;
        return;
      }

      const invitations = await getPendingInvitationsForEmail(email);
      if (invitations.length === 0) {
        // No invitation — sign out before onAuthStateChanged processes the user
        await signOut(auth);
        loginProcessingRef.current = false;
        await new Promise((resolve) => setTimeout(resolve, 500));
        throw new Error('Signup is invite-only. Please contact your organization administrator for an invitation.');
      }

      // Invited new user — create the profile. No signup-form consent exists on
      // this path, so legal stays unstamped and the post-login legal gate is the
      // fallback (same as a Login-page Google sign-in). onAuthStateChanged
      // performs the server-side invite accept.
      const newProfile = await createUserProfile(userCredential.user);
      setUserProfile(newProfile);
      loginProcessingRef.current = false;
    } catch (error) {
      loginProcessingRef.current = false;
      throw error;
    }
  };

  const signup = async (email: string, password: string, displayName?: string) => {
    // Hold off onAuthStateChanged's bootstrap until account + profile are ready
    // and consent is captured, so it accepts the invite (server-side) cleanly.
    loginProcessingRef.current = true;
    try {
      // Create the account FIRST, then verify the invitation while authenticated.
      // The `invitations` collection is only readable by an authenticated user
      // (firestore.rules), so the old pre-account check always came back
      // permission-denied → getPendingInvitationsForEmail fails closed to [] →
      // a false "invite-only" error. This was the ONE path that checked the
      // invite pre-auth; loginWithGoogle + magic-link both check post-auth, which
      // is why only email/password signup was broken for real invitees.
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Now authenticated — this read succeeds under the security rules.
      const invitations = await getPendingInvitationsForEmail(email);
      if (invitations.length === 0) {
        // No invitation — undo the account we just created so a rejected signup
        // never leaves an orphaned Auth user (and, critically, never gets a
        // profile doc, which would trip the onAuthStateChanged "existing user"
        // bypass and let a non-invitee in). Then surface the invite-only gate.
        try {
          await user.delete();
        } catch {
          await signOut(auth);
        }
        throw new Error('Signup is invite-only. Please contact your organization administrator for an invitation.');
      }

      // Invited — create the profile now. Stamp legal at creation time (the
      // registration form required the terms checkbox) so the profile is never
      // persisted without consent.
      const newProfile = await createUserProfile(user, displayName, CURRENT_LEGAL_VERSION);
      setUserProfile(newProfile);

      // The registration form required the terms checkbox — capture that consent
      // so onAuthStateChanged's server-side accept stamps legal acceptance. This
      // means the user is NOT re-prompted by the post-login legal gate.
      pendingConsentRef.current = { version: CURRENT_LEGAL_VERSION, docs: acceptedDocsSnapshot() };
      // onAuthStateChanged (already firing from createUserWithEmailAndPassword)
      // performs the invite acceptance + org switch once this resolves.
    } finally {
      loginProcessingRef.current = false;
    }
  };

  const loginWithGoogle = async (acceptedLegalVersion?: string) => {
    // Set flag to indicate we're processing login
    loginProcessingRef.current = true;
    
    try {
      const provider = new GoogleAuthProvider();
      const userCredential = await signInWithPopup(auth, provider);
      const email = userCredential.user.email!;

      // NB: removed client-side BOOTSTRAP_SUPER_ADMINS self-escalation.
      // Privilege fields (systemRole / isSuperAdmin / role) must be set
      // server-side only — Firestore rules now reject client writes that
      // mutate these on the user's own doc. Bootstrap a super-admin via
      // an Admin-SDK script or a Cloud Function called by an existing
      // super-admin.

      // CRITICAL: Check if user already exists (has a profile doc OR organizations)
      const userRef = doc(db, 'users', userCredential.user.uid);
      const userDoc = await getDoc(userRef);
      if (userDoc.exists()) {
        // User profile exists — they're an existing user, let onAuthStateChanged handle the rest
        loginProcessingRef.current = false;
        return;
      }

      const existingOrgs = await loadUserOrganizations(userCredential.user.uid);
      if (existingOrgs.length > 0) {
        // User already exists - allow login
        // Note: onAuthStateChanged will handle the rest
        loginProcessingRef.current = false;
        return;
      }

      // CRITICAL: New user - must have invitation
      const invitations = await getPendingInvitationsForEmail(email);

      if (invitations.length === 0) {
        // DEV BYPASS: when VITE_DEV_BYPASS_INVITE=true, auto-create profile + org
        // so developers can log in without a formal invitation. Never set this in prod.
        if (import.meta.env.DEV && import.meta.env.VITE_DEV_BYPASS_INVITE === 'true') {
          console.warn('[AuthContext] DEV BYPASS: auto-creating profile + org for', userCredential.user.email);
          await createUserProfile(userCredential.user);
          await createOrganization(
            userCredential.user.displayName
              ? `${userCredential.user.displayName}'s Org`
              : 'My Organization',
            userCredential.user
          );
          loginProcessingRef.current = false;
          return;
        }

        // No invitation - sign them out IMMEDIATELY and throw error
        // This must happen before onAuthStateChanged processes the user
        await signOut(auth);
        loginProcessingRef.current = false;
        // Wait a moment to ensure sign-out completes
        await new Promise(resolve => setTimeout(resolve, 500));
        throw new Error('Signup is invite-only. Please contact your organization administrator for an invitation.');
      }
      
      // User has invitation - create profile, stamping legal at creation time when
      // the Signup page captured terms consent (so it survives regardless of the
      // async-accept timing). undefined version (Login page) leaves it unstamped
      // and the legal gate remains the fallback.
      const newProfile = await createUserProfile(userCredential.user, undefined, acceptedLegalVersion);
      setUserProfile(newProfile);

      // Also carry the consent to the server-side accept so it can bind the org
      // MSA for owner/admin invites (and as a backstop for the user legal stamp).
      if (acceptedLegalVersion) {
        pendingConsentRef.current = { version: acceptedLegalVersion, docs: acceptedDocsSnapshot() };
      }

      // Don't accept invitation here - let onAuthStateChanged handle it
      // This prevents race conditions
      loginProcessingRef.current = false;
    } catch (error) {
      loginProcessingRef.current = false;
      throw error;
    }
  };

  const logout = async () => {
    // The user manual's session cookie is HttpOnly, so only its server can clear it.
    fetch('/user-manual/session', { method: 'DELETE', credentials: 'same-origin', keepalive: true }).catch(() => {});
    await signOut(auth);
    setUserProfile(null);
    setOrganizations([]);
    setActiveOrganization(null);
    setActiveOrganizationId(null);
    setUserRole(null);
    setIsViewingAsOrg(false);
    setViewedAsOrg(null);
    setNeedsLegalAcceptance(false);
    // Clear 2FA session state so the next login re-challenges.
    mfaTokenStore.clear();
    setMfaBlocking(false);
    setMfaNag(false);
    setMfaEnrollmentRequired(false);
    setMfaMethods([]);
    setMfaGraceEndsAt(null);
    localStorage.removeItem('activeOrganizationId');
    localStorage.removeItem('viewAsOrgId');
  };

  // Apply a /auth/mfa/session response to gate state + the token store.
  const applyMfaSession = useCallback((res: Awaited<ReturnType<typeof mfaSessionApi>>) => {
    setMfaGraceEndsAt(res.graceEndsAtMs ?? null);
    if (!res.challenge) {
      if (res.satisfied && res.mfaToken) mfaTokenStore.set(res.mfaToken);
      setMfaBlocking(false);
      setMfaNag(false);
      setMfaEnrollmentRequired(false);
      setMfaMethods(res.methods ?? []);
      return;
    }
    setMfaEnrollmentRequired(!!res.enrollmentRequired);
    setMfaMethods(res.methods ?? []);
    setMfaRecoveryAvailable(!!res.recoveryAvailable);
    if (res.blocking) { setMfaBlocking(true); setMfaNag(false); }
    else { setMfaBlocking(false); setMfaNag(true); }
  }, []);

  const refreshMfaSession = useCallback(async () => {
    if (!auth.currentUser) return;
    try {
      const res = await mfaSessionApi(activeOrganizationId ?? undefined);
      applyMfaSession(res);
    } catch (e) {
      console.warn('[mfa] session refresh failed:', e);
    }
  }, [activeOrganizationId, applyMfaSession]);

  // Called by the gate after a successful challenge.
  const onMfaSatisfied = useCallback((token: string, _expiresAtMs: number) => {
    mfaTokenStore.set(token);
    setMfaBlocking(false);
    setMfaNag(false);
  }, []);

  // Bootstrap the 2FA session once a user is logged in — NOT gated on an active
  // org, so a member-less account (a super-admin working outside a tenant) gets
  // the same treatment as everyone else.
  //
  // This makes the full-screen gate reachable for those accounts, which is only
  // safe because the challenge can no longer dead-end: recovery codes are always
  // offered as a fallback, and a passkey never auto-leads on a device with no
  // local authenticator. Both are preconditions — don't keep this without them.
  // Re-runs when the org resolves, since org policy can raise the requirement.
  useEffect(() => {
    if (loading || !currentUser) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await mfaSessionApi(activeOrganizationId ?? undefined);
        if (!cancelled) applyMfaSession(res);
      } catch (e) {
        if (!cancelled) console.warn('[mfa] session bootstrap failed:', e);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, currentUser?.uid, activeOrganizationId]);

  // Fallback: an API call 403'd with mfa_required. Re-run the authoritative
  // session check rather than blindly raising the gate — on a trusted device it
  // silently mints a token (no gate flash), on a new/untrusted device it raises
  // the gate that stays. `/auth/mfa/session` is enforcement-exempt so this can't
  // loop.
  useEffect(() => {
    mfaTokenStore.setOnRequired(() => { void refreshMfaSession(); });
    return () => mfaTokenStore.setOnRequired(null);
  }, [refreshMfaSession]);

  const resetPassword = async (email: string) => {
    await sendPasswordResetEmail(auth, email);
  };

  // ============================================================================
  // ORGANIZATION METHODS
  // ============================================================================

  const switchOrganization = async (organizationId: string, user?: User, userOrgs?: OrganizationMembership[]) => {
    const activeUser = user || currentUser;
    if (!activeUser) throw new Error('No user logged in');

    // Load organization details
    const org = await loadOrganizationDetails(organizationId);
    if (!org) {
      throw new Error('Organization not found');
    }

    // Try to load role from the org members subcollection (authoritative).
    // Catch permission-denied in case Firestore rules haven't been deployed yet.
    let role: UserRole | null = null;
    try {
      role = await loadUserRoleInOrganization(organizationId, activeUser.uid);
    } catch (permErr: any) {
      console.warn('[switchOrganization] Member doc read failed (rules may not be deployed):', permErr?.code);
    }

    // Fallback 1: already-loaded organizations list (no extra Firestore read)
    if (!role) {
      const orgInList = (userOrgs || organizations).find(o => o.organizationId === organizationId);
      if (orgInList?.role) {
        role = orgInList.role as UserRole;
      }
    }

    // Fallback 2: userProfile.organizations array field (admin-created users)
    if (!role) {
      const activeProfile = userProfile || (await loadUserProfile(activeUser));
      const profileOrgEntry = (activeProfile as any)?.organizations?.find(
        (o: any) => o.organizationId === organizationId
      );
      if (profileOrgEntry?.role) {
        role = profileOrgEntry.role as UserRole;
      }
    }

    // Fallback 3: user's own organizations subcollection (always readable by the user themselves)
    if (!role) {
      try {
        const userOrgRef = doc(db, 'users', activeUser.uid, 'organizations', organizationId);
        const userOrgDoc = await getDoc(userOrgRef);
        if (userOrgDoc.exists()) {
          role = userOrgDoc.data().role as UserRole;
        }
      } catch (e) {
        console.warn('[switchOrganization] Could not read user org subcollection:', e);
      }
    }

    if (!role) {
      throw new Error('You are not a member of this organization');
    }

    // Update state
    setActiveOrganization(org);
    setActiveOrganizationId(organizationId);
    setUserRole(role);
    setNeedsOrgSelection(false);

    // Persist to localStorage
    localStorage.setItem('activeOrganizationId', organizationId);

    // Update user's last organization
    if (userProfile) {
      await updateDoc(doc(db, 'users', activeUser.uid), {
        'metadata.lastOrganizationId': organizationId,
      });
    }
  };

  const createOrganization = async (name: string, user?: User): Promise<string> => {
    const activeUser = user || currentUser;
    if (!activeUser) throw new Error('No user logged in');

    const batch = writeBatch(db);
    const orgRef = doc(collection(db, 'organizations'));
    const orgId = orgRef.id;

    // Create organization document
    const newOrg: Omit<Organization, 'id'> = {
      name,
      slug: generateSlug(name),
      ownerId: activeUser.uid,
      logoURL: null,
      createdAt: serverTimestamp() as Timestamp,
      updatedAt: serverTimestamp() as Timestamp,
      settings: {
        allowSelfSignup: false,
        defaultRole: 'member',
        requireEmailVerification: false,
        allowedDomains: [],
      },
      subscription: {
        plan: 'starter',
        status: 'trial',
        billingPeriodStart: Timestamp.fromDate(new Date()),
        billingPeriodEnd: Timestamp.fromDate(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)),
      },
    };
    batch.set(orgRef, newOrg);

    // Add user as owner in members subcollection
    const memberRef = doc(db, 'organizations', orgId, 'members', activeUser.uid);
    batch.set(memberRef, {
      role: 'owner',
      joinedAt: serverTimestamp(),
      invitedBy: null,
      email: activeUser.email,
      displayName: activeUser.displayName || null,
      photoURL: activeUser.photoURL || null,
      lastSeenAt: serverTimestamp(),
    });

    // Add organization to user's organizations subcollection
    const userOrgRef = doc(db, 'users', activeUser.uid, 'organizations', orgId);
    batch.set(userOrgRef, {
      role: 'owner',
      organizationName: name,
      organizationSlug: generateSlug(name),
      organizationLogoURL: null,
      joinedAt: serverTimestamp(),
      isFavorite: true,
    });

    await batch.commit();

    // Refresh organizations list
    await refreshOrganizations();

    return orgId;
  };

  const updateOrganization = async (updates: Partial<Organization>) => {
    if (!activeOrganizationId) throw new Error('No active organization');
    if (!hasPermission('org:update-settings')) {
      throw new Error('You do not have permission to update organization settings');
    }

    const orgRef = doc(db, 'organizations', activeOrganizationId);
    await updateDoc(orgRef, {
      ...updates,
      updatedAt: serverTimestamp(),
    });

    // Reload organization details
    const updatedOrg = await loadOrganizationDetails(activeOrganizationId);
    if (updatedOrg) {
      setActiveOrganization(updatedOrg);
    }
  };

  // ============================================================================
  // VIEW AS ORGANIZATION METHODS
  // ============================================================================

  const viewAsOrganization = async (organizationId: string, reason: string) => {
    if (!isSuperAdmin) {
      throw new Error('Only super admins can view as organization');
    }

    try {
      // Load the organization details
      const org = await loadOrganizationDetails(organizationId);
      if (!org) {
        throw new Error('Organization not found');
      }

      // Write audit log directly to Firestore
      try {
        await addDoc(collection(db, 'audit_logs'), {
          timestamp: serverTimestamp(),
          actorId: currentUser!.uid,
          actorEmail: currentUser!.email,
          actorSystemRole: userProfile?.systemRole || null,
          action: 'admin.view_as_organization_start',
          resource: organizationId,
          resourceType: 'organization',
          organizationId: organizationId,
          metadata: { reason, organizationName: org.name },
          success: true,
          actorIpAddress: null, // the browser cannot see its own egress IP
          actorUserAgent: navigator.userAgent
        });
      } catch (auditError: any) {
        console.error('[viewAsOrganization] Failed to write audit log:', auditError);
        // Continue even if audit logging fails
      }

      // Set view-as-org state
      setIsViewingAsOrg(true);
      setViewedAsOrg(org);
      setActiveOrganization(org);
      setActiveOrganizationId(organizationId);

      // Set a synthetic role (super admins viewing as org are treated as owners)
      setUserRole('owner');

      // Persist to localStorage so it survives page refresh
      localStorage.setItem('viewAsOrgId', organizationId);
    } catch (error: any) {
      console.error('[viewAsOrganization] Error:', error);
      throw error;
    }
  };

  const stopViewingAsOrg = async () => {
    if (!isViewingAsOrg || !viewedAsOrg) {
      return; // Nothing to stop
    }

    try {
      // Write audit log directly to Firestore
      try {
        await addDoc(collection(db, 'audit_logs'), {
          timestamp: serverTimestamp(),
          actorId: currentUser!.uid,
          actorEmail: currentUser!.email,
          actorSystemRole: userProfile?.systemRole || null,
          action: 'admin.view_as_organization_stop',
          resource: viewedAsOrg.id,
          resourceType: 'organization',
          organizationId: viewedAsOrg.id,
          metadata: { organizationName: viewedAsOrg.name },
          success: true,
          actorIpAddress: null, // the browser cannot see its own egress IP
          actorUserAgent: navigator.userAgent
        });
      } catch (auditError: any) {
        console.error('[stopViewingAsOrg] Failed to write audit log:', auditError);
        // Continue even if audit logging fails
      }

      // Clear view-as-org state
      setIsViewingAsOrg(false);
      setViewedAsOrg(null);

      // System admins don't need to have an active organization
      setActiveOrganization(null);
      setActiveOrganizationId(null);
      setUserRole(null);

      // Clear from localStorage
      localStorage.removeItem('viewAsOrgId');
    } catch (error: any) {
      console.error('[stopViewingAsOrg] Error:', error);
      throw error;
    }
  };

  // ============================================================================
  // USER MANAGEMENT METHODS
  // ============================================================================

  const inviteUser = async (email: string, role: Exclude<UserRole, 'owner'>) => {
    if (!activeOrganizationId || !currentUser || !userProfile) {
      throw new Error('No active organization');
    }
    if (!hasPermission('users:invite')) {
      throw new Error('You do not have permission to invite users');
    }

    const normalizedEmail = email.toLowerCase();

    // If a pending invite already exists for this email+org, delete it so the
    // new addDoc below re-fires onInvitationCreated and a fresh email is sent.
    // (Updating in place would leave the email trigger silent.)
    const existingInviteQuery = query(
      collection(db, 'invitations'),
      where('organizationId', '==', activeOrganizationId),
      where('email', '==', normalizedEmail),
      where('status', '==', 'pending')
    );
    const existingInvites = await getDocs(existingInviteQuery);
    await Promise.all(existingInvites.docs.map((d) => deleteDoc(d.ref)));

    const invitation: Omit<Invitation, 'id'> = {
      organizationId: activeOrganizationId,
      organizationName: activeOrganization!.name,
      email: normalizedEmail,
      role,
      invitedBy: currentUser.uid,
      invitedByName: userProfile.displayName || userProfile.email,
      status: 'pending',
      token: generateInvitationToken(),
      createdAt: serverTimestamp() as Timestamp,
      expiresAt: Timestamp.fromDate(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)), // 7 days
      acceptedAt: null,
    };

    await addDoc(collection(db, 'invitations'), invitation);

    // TODO: Send invitation email via Firebase Functions
  };

  const removeUser = async (userId: string) => {
    if (!activeOrganizationId || !currentUser) {
      throw new Error('No active organization');
    }
    if (!hasPermission('users:remove')) {
      throw new Error('You do not have permission to remove users');
    }
    if (userId === currentUser.uid) {
      throw new Error('You cannot remove yourself');
    }

    const batch = writeBatch(db);

    // Remove from organization members (read email first so we can clean up invitations)
    const memberRef = doc(db, 'organizations', activeOrganizationId, 'members', userId);
    const memberSnap = await getDoc(memberRef);
    const memberEmail = (memberSnap.exists() ? (memberSnap.data() as any).email : undefined) as
      | string
      | undefined;
    batch.delete(memberRef);

    // Remove from user's organizations
    const userOrgRef = doc(db, 'users', userId, 'organizations', activeOrganizationId);
    batch.delete(userOrgRef);

    // Clean up any pending invitations for this email in this org
    if (memberEmail) {
      const staleInvitesQuery = query(
        collection(db, 'invitations'),
        where('organizationId', '==', activeOrganizationId),
        where('email', '==', memberEmail.toLowerCase()),
        where('status', '==', 'pending')
      );
      const staleInvites = await getDocs(staleInvitesQuery);
      staleInvites.forEach((inviteDoc) => batch.delete(inviteDoc.ref));
    }

    await batch.commit();
  };

  const updateUserRole = async (userId: string, newRole: UserRole) => {
    if (!activeOrganizationId || !currentUser) {
      throw new Error('No active organization');
    }
    if (!hasPermission('users:change-role')) {
      throw new Error('You do not have permission to change user roles');
    }
    if (userId === currentUser.uid) {
      throw new Error('You cannot change your own role');
    }
    if (newRole === 'owner') {
      throw new Error('Cannot assign owner role. Use transfer ownership instead.');
    }

    const batch = writeBatch(db);

    // Update role in organization members
    const memberRef = doc(db, 'organizations', activeOrganizationId, 'members', userId);
    batch.update(memberRef, { role: newRole });

    // Update role in user's organizations
    const userOrgRef = doc(db, 'users', userId, 'organizations', activeOrganizationId);
    batch.update(userOrgRef, { role: newRole });

    await batch.commit();
  };

  // Accept an invitation SERVER-SIDE (Admin SDK transaction). The client no
  // longer does rule-gated batch writes for onboarding — that's what stranded
  // new invitees at the legal gate with permission-denied. The endpoint creates
  // the profile if missing, stamps legal from `consent`, writes the membership,
  // marks the invite accepted, and binds the org MSA for owner/admin — atomically.
  const acceptInvitationViaServer = async (
    token: string,
    consent?: { version: string; docs: Record<string, string> } | null
  ): Promise<{ organizationId: string; role: UserRole; organizationName?: string }> => {
    const idToken = await auth.currentUser?.getIdToken();
    if (!idToken) throw new Error('You must be logged in to accept an invitation');

    const API_BASE = API_URL;
    const resp = await fetch(`${API_BASE}/organizations/accept-invitation`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({
        token,
        acceptedLegalVersion: consent?.version,
        acceptedDocs: consent?.docs,
        orgLegalVersion: consent ? ORG_LEGAL_VERSION : undefined,
      }),
    });
    const data = await resp.json().catch(() => ({}));
    if (!resp.ok) {
      throw new Error(data.message || data.error || `Could not accept invitation (${resp.status})`);
    }
    return data;
  };

  const acceptInvitation = async (token: string) => {
    if (!currentUser) {
      throw new Error('You must be logged in to accept an invitation');
    }
    // Treat an interactive accept as fresh consent (this path is reached from
    // surfaces where the user has agreed to terms).
    const result = await acceptInvitationViaServer(token, {
      version: CURRENT_LEGAL_VERSION,
      docs: acceptedDocsSnapshot(),
    });

    // Refresh organizations and switch to the newly joined org
    await refreshOrganizations();
    await switchOrganization(result.organizationId);
  };

  const getPendingInvitations = async (): Promise<Invitation[]> => {
    if (!currentUser) return [];

    const inviteQuery = query(
      collection(db, 'invitations'),
      where('email', '==', currentUser.email!.toLowerCase()),
      where('status', '==', 'pending')
    );
    const inviteSnapshot = await getDocs(inviteQuery);

    return inviteSnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as Invitation[];
  };

  const getPendingInvitationsForOrg = async (organizationId: string): Promise<Invitation[]> => {
    const inviteQuery = query(
      collection(db, 'invitations'),
      where('organizationId', '==', organizationId),
      where('status', '==', 'pending')
    );
    const inviteSnapshot = await getDocs(inviteQuery);
    return inviteSnapshot.docs
      .map((d) => ({ id: d.id, ...d.data() }) as Invitation)
      .sort((a, b) => (b.createdAt?.toMillis?.() ?? 0) - (a.createdAt?.toMillis?.() ?? 0));
  };

  const revokeInvitation = async (invitationId: string): Promise<void> => {
    await deleteDoc(doc(db, 'invitations', invitationId));
  };

  const getPendingInvitationsForEmail = async (email: string): Promise<Invitation[]> => {
    try {
      const inviteQuery = query(
        collection(db, 'invitations'),
        where('email', '==', email.toLowerCase()),
        where('status', '==', 'pending')
      );
      const inviteSnapshot = await getDocs(inviteQuery);

      const now = Date.now();
      const validInvitations = inviteSnapshot.docs
        .map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
          };
        })
        .filter((invite) => {
          const expiresAt = (invite as Invitation).expiresAt?.toMillis();
          return expiresAt ? expiresAt > now : false;
        }) as Invitation[];

      return validInvitations;
    } catch (error) {
      console.error('[getPendingInvitationsForEmail] Error querying invitations:', error);
      // If query fails, return empty array (fail closed - no invitations = no access)
      return [];
    }
  };

  // ============================================================================
  // PERMISSION HELPERS
  // ============================================================================

  const hasPermission = useCallback(
    (permission: Permission): boolean => {
      if (!userRole) return false;
      return checkPermission(userRole, permission);
    },
    [userRole]
  );

  const canManageUsers = useCallback((): boolean => {
    return hasPermission('users:invite');
  }, [hasPermission]);

  const canManageResources = useCallback((): boolean => {
    return hasPermission('flows:edit-all');
  }, [hasPermission]);

  const canEditResource = useCallback(
    (createdBy: string): boolean => {
      if (!currentUser || !userRole) return false;

      // Owner and admin can edit any resource
      if (userRole === 'owner' || userRole === 'admin') {
        return true;
      }

      // Members can only edit their own resources
      if (userRole === 'member') {
        return currentUser.uid === createdBy;
      }

      // Viewers cannot edit anything
      return false;
    },
    [currentUser, userRole]
  );

  const canDeleteResource = useCallback(
    (createdBy: string): boolean => {
      return canEditResource(createdBy);
    },
    [canEditResource]
  );

  // ============================================================================
  // EFFECTS
  // ============================================================================

  // Listen to Firebase auth state
  useEffect(() => {
    // CRITICAL: Ensure loading starts as true
    setLoading(true);
    
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);

      if (user) {
        // CRITICAL: Check if user is anonymous - we don't allow anonymous access
        if (user.isAnonymous) {
          await signOut(auth);
          setLoading(false);
          return;
        }

        // Re-assert loading for a genuine login/signup (NOT an established-session
        // token re-fire). This keeps the legal gate and routes waiting for the full
        // bootstrap instead of rendering against a half-loaded profile — during
        // signup the profile exists for a few seconds before the server-side
        // invite-accept stamps its legal record, which otherwise flashes the gate.
        // Established re-fires (establishedUidRef === uid) are left untouched so a
        // silent token refresh never disrupts a working session.
        if (establishedUidRef.current !== user.uid) {
          setLoading(true);
        }

        try {
          // Force-fetch the ID token so the Firestore SDK has it before any reads.
          // onAuthStateChanged can fire before the token propagates, causing
          // permission-denied on the first getDoc even though rules should allow it.
          await user.getIdToken();

          // Load user profile
          console.log('[AuthContext:debug] step 1 – loadUserProfile', user.uid);
          const profile = await loadUserProfile(user);
          console.log('[AuthContext:debug] step 1 OK', { hasProfile: !!profile });
          setUserProfile(profile);

          // Decide whether to show the legal-acceptance gate. The gate is only
          // rendered (App.tsx) once org selection resolves and the user is on a
          // non-legal route, so setting it here is safe even though org state is
          // assigned later. Any branch that signs the user out re-fires this
          // listener with user === null, which resets the flag below.
          setNeedsLegalAcceptance(!hasAcceptedLegal(profile));

          // CRITICAL: If loginWithGoogle is processing, wait for it to complete
          // This prevents race conditions where onAuthStateChanged processes new users
          // before loginWithGoogle can check for invitations and sign them out
          if (loginProcessingRef.current) {
            // Wait up to 5 seconds for loginWithGoogle to complete
            let waitCount = 0;
            while (loginProcessingRef.current && waitCount < 50) {
              await new Promise(resolve => setTimeout(resolve, 100));
              waitCount++;
            }

            // CRITICAL: Re-check if user still exists (loginWithGoogle might have signed them out)
            if (!auth.currentUser) {
              setCurrentUser(null);
              setUserProfile(null);
              setOrganizations([]);
              setActiveOrganization(null);
              setActiveOrganizationId(null);
              setUserRole(null);
              setLoading(false);
              return;
            }
          }

          // Load user's organizations
          console.log('[AuthContext:debug] step 2 – loadUserOrganizations');
          const userOrgs = await loadUserOrganizations(user.uid);
          console.log('[AuthContext:debug] step 2 OK', { orgsCount: userOrgs.length });
          setOrganizations(userOrgs);

          // CRITICAL: If user has no organizations, check for invitation BEFORE proceeding
          // This is invite-only - new users MUST have an invitation
          if (userOrgs.length === 0) {
            // CRITICAL: Double-check - wait a moment in case loginWithGoogle is still processing
            if (loginProcessingRef.current) {
              await new Promise(resolve => setTimeout(resolve, 1000));

              // Re-check if user still exists
              if (!auth.currentUser) {
                setCurrentUser(null);
                setUserProfile(null);
                setOrganizations([]);
                setActiveOrganization(null);
                setActiveOrganizationId(null);
                setUserRole(null);
                setLoading(false);
                return;
              }

              // Re-check organizations (might have been created)
              const recheckOrgs = await loadUserOrganizations(user.uid);
              if (recheckOrgs.length > 0) {
                setOrganizations(recheckOrgs);
                // Continue with normal flow below
                const orgToActivate = recheckOrgs[0].organizationId;
                await switchOrganization(orgToActivate, user, recheckOrgs);
                setLoading(false);
                return;
              }
            }

            // Check for pending invitations
            const invitations = await getPendingInvitationsForEmail(user.email!);

            // RBAC: System admins don't need organizations
            const isSystemAdmin = (profile as any)?.systemRole !== null && (profile as any)?.systemRole !== undefined;

            // Existing users (profile doc exists) should never be signed out even if org list is empty
            console.log('[AuthContext:debug] step 3 – isExistingUser check');
            const userRef = doc(db, 'users', user.uid);
            const userDoc = await getDoc(userRef);
            const isExistingUser = userDoc.exists();
            console.log('[AuthContext:debug] step 3 OK', { isExistingUser });

            if (invitations.length === 0 && !isSystemAdmin && !isExistingUser) {
              // CRITICAL: No invitation and no organizations = unauthorized user
              // Sign them out immediately - this is invite-only
              // EXCEPTION: System admins (super_admin, support) are allowed without organizations
              await signOut(auth);
              setCurrentUser(null);
              setUserProfile(null);
              setOrganizations([]);
              setActiveOrganization(null);
              setActiveOrganizationId(null);
              setUserRole(null);
              setLoading(false);
              return;
            }

            // System admin logged in without organizations - this is OK
            if (isSystemAdmin && invitations.length === 0) {
              // Check if super admin was viewing as an organization before refresh
              const savedViewAsOrgId = localStorage.getItem('viewAsOrgId');
              if (savedViewAsOrgId && (profile as any)?.systemRole === 'super_admin') {
                try {
                  const viewAsOrg = await loadOrganizationDetails(savedViewAsOrgId);
                  if (viewAsOrg) {
                    setIsViewingAsOrg(true);
                    setViewedAsOrg(viewAsOrg);
                    setActiveOrganization(viewAsOrg);
                    setActiveOrganizationId(savedViewAsOrgId);
                    setUserRole('owner');
                  } else {
                    // Organization no longer exists, clear the saved state
                    localStorage.removeItem('viewAsOrgId');
                  }
                } catch (error) {
                  console.error('[AuthContext] Error restoring view-as-org:', error);
                  localStorage.removeItem('viewAsOrgId');
                }
              }

              // System-admin session established (incl. super-admin viewing-as-org,
              // e.g. shvartzj@gmail.com). Mark it so a token-refresh re-fire that
              // hits a transient read doesn't sign them out.
              establishedUidRef.current = user.uid;
              setLoading(false);
              return;
            }

            // User has a valid invitation — accept it SERVER-SIDE (Admin SDK,
            // single transaction). The client no longer does rule-gated batch
            // writes here; that's what produced the permission-denied sign-outs
            // and stranded new invitees at the legal gate. The endpoint creates
            // the profile if missing, stamps legal from the captured consent,
            // writes the membership, marks the invite accepted, and binds the
            // org MSA for owner/admin — atomically.
            try {
              const consent = pendingConsentRef.current;
              await acceptInvitationViaServer(invitations[0].token, consent);
              pendingConsentRef.current = null;

              // Reload organizations + the (now possibly legal-stamped) profile
              const reloadedOrgs = await loadUserOrganizations(user.uid);
              if (reloadedOrgs.length > 0) {
                setOrganizations(reloadedOrgs);
                await switchOrganization(reloadedOrgs[0].organizationId, user, reloadedOrgs);
                // Refresh profile so a legal stamp written server-side is
                // reflected — keeps the post-login gate from showing for users
                // who already consented on the registration form.
                const refreshed = await loadUserProfile(user);
                setUserProfile(refreshed);
                setNeedsLegalAcceptance(!hasAcceptedLegal(refreshed));
                establishedUidRef.current = user.uid;
              } else {
                console.error('[AuthContext] Organization not found after accepting invitation');
                await signOut(auth);
                setCurrentUser(null);
                setUserProfile(null);
                setLoading(false);
                return;
              }
            } catch (inviteError) {
              console.error('[AuthContext] Error accepting invitation:', inviteError);
              await signOut(auth);
              setCurrentUser(null);
              setUserProfile(null);
              setLoading(false);
              return;
            }

            // After accepting invitation, we're done - return early
            setLoading(false);
            return;
          }

          // User has organizations - proceed with normal flow
          // Repair any missing member docs first so Firestore security rules pass
          console.log('[AuthContext:debug] step 4 – repairMemberships + switchOrg');
          await repairMembershipsIfNeeded();

          // Set active organization OR require invitation if none exist
          if (userOrgs.length > 0) {
            const savedOrgId = localStorage.getItem('activeOrganizationId');
            const lastOrgId = profile?.metadata.lastOrganizationId;
            const hasPreference =
              (savedOrgId && userOrgs.find((o) => o.organizationId === savedOrgId)) ||
              (lastOrgId && userOrgs.find((o) => o.organizationId === lastOrgId));

            if (userOrgs.length === 1 || hasPreference) {
              const orgToActivate =
                (savedOrgId && userOrgs.find((o) => o.organizationId === savedOrgId)
                  ? savedOrgId
                  : lastOrgId && userOrgs.find((o) => o.organizationId === lastOrgId)
                  ? lastOrgId
                  : userOrgs[0].organizationId);
              console.log('[AuthContext:debug] step 5 – switchOrganization', orgToActivate);
              await switchOrganization(orgToActivate, user, userOrgs);
              console.log('[AuthContext:debug] step 5 OK');
            } else {
              // Multiple orgs, no saved preference → ask user to choose
              setOrganizations(userOrgs);
              setNeedsOrgSelection(true);
            }
            // Session fully established for this uid — mark it so a later
            // token-refresh re-fire that hits a transient read error keeps the
            // session instead of signing out.
            establishedUidRef.current = user.uid;
          } else {
            // This should never happen now, but keep as fallback
            console.error('[AuthContext] Unexpected: User has no organizations after invitation check');
            await signOut(auth);
            setCurrentUser(null);
            setUserProfile(null);
            setOrganizations([]);
            setActiveOrganization(null);
            setActiveOrganizationId(null);
            setUserRole(null);
            setLoading(false);
            return;
          }
        } catch (error: any) {
          console.error('[AuthContext] Error loading user data:', error);

          // DON'T sign out an already-established session on a transient error.
          // onAuthStateChanged re-fires on every silent ID-token refresh (~hourly);
          // if a Firestore read hiccups during that re-run, signing out here yanks
          // the user back to login mid-session for no real reason. Only the FIRST
          // load (no established session for this uid) signs out — a genuine
          // can't-load-your-account failure. A re-fire keeps the working session
          // and just logs the blip; the next refresh re-resolves cleanly.
          if (establishedUidRef.current === user.uid) {
            console.warn(
              '[AuthContext] Transient error during re-resolve of an established ' +
              'session — keeping session, not signing out.'
            );
            setLoading(false);
            return;
          }

          // Relay the real error to the login page so the user sees WHY they
          // were signed out — otherwise they get silently redirected and
          // can't tell the difference between "not logged in" and "something
          // broke loading your account".
          try {
            sessionStorage.setItem(
              'auth:lastError',
              JSON.stringify({
                message: error?.message || String(error),
                code: error?.code || null,
                at: new Date().toISOString(),
              })
            );
          } catch { /* non-fatal */ }
          // CRITICAL: First-load failure — sign out and require re-auth.
          establishedUidRef.current = null;
          await signOut(auth);
          setCurrentUser(null);
          setUserProfile(null);
        }
      } else {
        // CRITICAL: No user - clear all state
        establishedUidRef.current = null;
        setCurrentUser(null);
        setUserProfile(null);
        setOrganizations([]);
        setActiveOrganization(null);
        setActiveOrganizationId(null);
        setUserRole(null);
        setNeedsLegalAcceptance(false);
      }

      // CRITICAL: Only set loading to false AFTER auth check completes
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const refreshUserProfile = async () => {
    if (!currentUser) return;
    const userRef = doc(db, 'users', currentUser.uid);
    const userDoc = await getDoc(userRef);
    if (userDoc.exists()) {
      setUserProfile(userDoc.data() as UserProfile);
    }
  };

  /**
   * Record click-through acceptance of the legal documents and clear the gate.
   *
   * Always writes the per-user record on `users/{uid}.legal`. Additionally
   * writes the org-level MSA binding on `organizations/{activeOrg}.legal` when
   * the accepting user is an owner/admin of the active org and is NOT a
   * super-admin impersonating that org (`isViewingAsOrg`) — we never bind a
   * tenant's MSA on someone else's behalf. Org-binding failure is non-fatal:
   * the per-user record is the gating write.
   */
  const acceptLegal = async () => {
    // Only the authenticated user is required — NOT a loaded userProfile. An
    // invited user whose profile doc is somehow missing must still be able to
    // clear the gate (otherwise they dead-end here forever).
    if (!currentUser) {
      throw new Error('Not authenticated');
    }

    const acceptedDocs = acceptedDocsSnapshot();
    // SHA-256 of the exact bundled text (issue #99 AC #4). Best-effort: an empty
    // map (non-secure context) must not block acceptance.
    let contentHashes: Record<string, string> = {};
    try {
      contentHashes = await computeLegalContentHashes();
    } catch (e) {
      console.warn('[AuthContext] legal content hashing failed (non-fatal):', e);
    }

    // (a) Server-authoritative acceptance — stamps users/{uid}.legal with the
    // server-observed IP + User-Agent + content hashes AND emits an
    // `eula.accepted` audit log (the client cannot write audit_logs itself, and
    // cannot forge its own IP/UA). The endpoint also binds the org-level MSA for
    // an owner/admin of the active org. See POST /organizations/accept-legal.
    let serverOk = false;
    try {
      const idToken = await auth.currentUser?.getIdToken();
      if (idToken) {
        const API_BASE = API_URL;
        const resp = await fetch(`${API_BASE}/organizations/accept-legal`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            acceptedLegalVersion: CURRENT_LEGAL_VERSION,
            orgLegalVersion: ORG_LEGAL_VERSION,
            acceptedDocs,
            contentHashes,
            organizationId:
              activeOrganizationId && !isViewingAsOrg ? activeOrganizationId : undefined,
          }),
        });
        serverOk = resp.ok;
        if (!resp.ok) {
          const data = await resp.json().catch(() => ({}));
          console.error('[AuthContext] accept-legal endpoint failed:', resp.status, data);
        }
      }
    } catch (e) {
      console.error('[AuthContext] accept-legal request error (will fall back):', e);
    }

    const userLegal = {
      eulaVersion: CURRENT_LEGAL_VERSION,
      acceptedAt: serverTimestamp(),
      acceptedDocs,
      ip: null as string | null,
    };

    // (b) Client-side fallback ONLY if the server endpoint was unreachable. Uses
    // setDoc+merge (NOT updateDoc): merge CREATES the user doc if missing and
    // UPDATES it otherwise. updateDoc on a non-existent doc is rejected as
    // permission-denied (the update rule needs an existing resource) — the trap
    // that stranded invitees at the gate. This path records no IP/UA/audit (the
    // client can't); the server path above is the authoritative one.
    if (!serverOk) {
      await setDoc(
        doc(db, 'users', currentUser.uid),
        {
          email: currentUser.email,
          displayName: userProfile?.displayName ?? currentUser.displayName ?? null,
          legal: userLegal,
        },
        { merge: true }
      );

      // Org-level MSA binding — owner/admin of the active org, real session only.
      if (
        activeOrganizationId &&
        !isViewingAsOrg &&
        (userRole === 'owner' || userRole === 'admin')
      ) {
        try {
          await updateDoc(doc(db, 'organizations', activeOrganizationId), {
            legal: {
              msaVersion: ORG_LEGAL_VERSION,
              acceptedByUid: currentUser.uid,
              acceptedByEmail: currentUser.email ?? null,
              acceptedAt: serverTimestamp(),
            },
            updatedAt: serverTimestamp(),
          });
        } catch (e) {
          console.error('[AuthContext] org-level legal binding failed (non-fatal):', e);
        }
      }
    }

    // Reflect locally (serverTimestamp resolves async; use a client stamp for UI)
    // and clear the gate. Build a minimal profile if none was loaded so the app
    // has a usable userProfile after a from-scratch acceptance.
    const localLegal = { ...userLegal, acceptedAt: Timestamp.now() };
    setUserProfile((prev) =>
      prev
        ? { ...prev, legal: localLegal }
        : ({
            id: currentUser.uid,
            email: currentUser.email ?? '',
            displayName: currentUser.displayName ?? null,
            legal: localLegal,
          } as unknown as UserProfile)
    );
    setNeedsLegalAcceptance(false);
  };

  // ============================================================================
  // CONTEXT VALUE
  // ============================================================================

  const value: AuthContextType = {
    // User state
    currentUser,
    user: currentUser, // Alias for compatibility
    userProfile,
    loading,

    // Organization state
    organizations,
    activeOrganization,
    activeOrganizationId,
    userRole,
    organizationsLoading,

    // RBAC: System role state
    systemRole,
    isSystemAdmin,
    isSuperAdmin,
    isSupport,
    hasSystemRole: (role: 'super_admin' | 'support') => systemRole === role,

    // Org selection state
    needsOrgSelection,

    // Legal acceptance gate
    needsLegalAcceptance,
    acceptLegal,
    mfaBlocking,
    mfaNag,
    mfaEnrollmentRequired,
    mfaMethods,
    mfaRecoveryAvailable,
    mfaGraceEndsAt,
    onMfaSatisfied,
    refreshMfaSession,

    // View as Organization state
    isViewingAsOrg,
    viewedAsOrg,

    // Auth methods
    login,
    signup,
    loginWithGoogle,
    logout,
    resetPassword,
    sendMagicLink,
    completeMagicLink,
    isMagicLinkUrl,

    // Organization methods
    switchOrganization,
    createOrganization,
    updateOrganization,
    refreshOrganizations,

    // View as Organization methods
    viewAsOrganization,
    stopViewingAsOrg,

    // User management methods
    inviteUser,
    removeUser,
    updateUserRole,
    acceptInvitation,
    getPendingInvitations,
    getPendingInvitationsForOrg,
    revokeInvitation,

    // Permission helpers
    hasPermission,
    canManageUsers,
    canManageResources,
    canEditResource,
    canDeleteResource,

    // Profile helpers
    refreshUserProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

